import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faCircleCheck,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

/**
 * "Attention Required" — the things this engineer should act on first.
 *
 * Composed ONLY from counts the backend genuinely reports. There is no derived
 * urgency, no invented "overdue" rule and no placeholder row: an item whose
 * count is zero is not rendered at all, and if every item is zero the section
 * collapses to a single reassuring line rather than a wall of zeros.
 *
 * Each row carries its own destination, so the operator can act without hunting
 * through the application. A row is only ever added by a caller that has a real
 * route for it — there are deliberately no non-navigating rows.
 */

export interface AttentionItem {
  /** Stable key, also used as the test id suffix. */
  id: string;
  icon: IconDefinition;
  label: string;
  /** Real backend count. Rows with 0 are filtered out by the component. */
  count: number;
  /** Where clicking takes the operator. Required — no dead rows. */
  to: string;
  /** Short clarification of what the number means. */
  hint?: string;
  /**
   * Elevated treatment for genuinely blocking work. Restrained on purpose: a
   * subtle amber tint, not a wall of red.
   */
  emphasis?: boolean;
}

export function AttentionRequired({ items }: { items: AttentionItem[] }) {
  const navigate = useNavigate();
  const actionable = items.filter((i) => i.count > 0);

  return (
    <section aria-labelledby="attention-heading" data-testid="attention-required">
      <h2
        id="attention-heading"
        className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        Attention Required
      </h2>

      {actionable.length === 0 ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3.5 text-sm text-muted-foreground">
          <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-primary" />
          Nothing needs your attention right now.
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {actionable.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => navigate(item.to)}
                data-testid={`attention-${item.id}`}
                className="flex w-full min-h-[52px] items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60 focus:outline-none focus-visible:bg-muted/60"
              >
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                    item.emphasis ? 'bg-amber-500/10 text-amber-600' : 'bg-primary/10 text-primary',
                  )}
                >
                  <FontAwesomeIcon icon={item.icon} className="h-3.5 w-3.5" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {item.label}
                  </span>
                  {item.hint && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {item.hint}
                    </span>
                  )}
                </span>

                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
                    item.emphasis
                      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                      : 'bg-primary/10 text-primary',
                  )}
                >
                  {item.count}
                </span>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="h-3 w-3 shrink-0 text-muted-foreground"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AttentionRequiredSkeleton() {
  return (
    <section>
      <div className="mb-3 h-3 w-36 animate-pulse rounded bg-muted motion-reduce:animate-none" />
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex min-h-[52px] items-center gap-3 px-4 py-3">
            <div className="h-8 w-8 shrink-0 animate-pulse rounded-lg bg-muted motion-reduce:animate-none" />
            <div className="h-3 w-40 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          </div>
        ))}
      </div>
    </section>
  );
}
